import React from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import ArrowRight from '@material-ui/icons/KeyboardArrowRight';
import {
  makeSelectPageIntros,
  makeSelectClientDetails,
} from 'containers/Main/selectors';
import { getAccentColor } from 'containers/Main/utils';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import { isClientAccepted, tagsMatches } from './utils';

const useStyles = makeStyles({
  root: {
    borderRadius: 5,
    border: '1px solid #E9ECED',
    padding: '18px 16px !important',
    borderLeftWidth: 4,
    borderLeftColor: props => (props.accent ? `#${props.accent}` : '#E9ECED'),
  },
  reference: {
    marginTop: 16,
  },
  marginTop: {
    marginTop: 20,
  },
  marginBottom: {
    marginBottom: 20,
  },
  modal: {
    marginTop: 0,
    marginBottom: 12,
  },
  horizontalMargin: {
    marginLeft: 20,
    marginRight: 20,
  },
  image: {
    width: 64,
    height: 64,
    borderRadius: 12,
    marginRight: 18,
  },
  assessmentsColor: {
    borderColor: '#CDD0D1 !important',
  },
  link: {
    fontFamily: 'MadaBold',
  },
  caret: {
    height: 16,
    width: 16,
    marginBottom: 2,
  },
  textBackground: {
    padding: '16px !important',
    backgroundColor: '#F6F6F6',
  },
  plain: {
    marginTop: 20,
    marginBottom: 20,
  },
});

const PageIntroBlock = ({ id, page, slug, type, modal, color, tags = [] }) => {
  const intros = useSelector(makeSelectPageIntros());
  const clientDetails = useSelector(makeSelectClientDetails());
  const accentColor = getAccentColor(clientDetails, null);
  const classes = useStyles({ accent: accentColor });

  let data;
  if (type === 'reference') {
    [data] = intros
      .filter(item => {
        if (!slug && !id) return false;

        if (slug) {
          const referencesObj = _get(item, 'fields.referencePages', [])
            .map(ref => {
              if (_get(ref, 'fields.slug'))
                return {
                  slug: _get(ref, 'fields.slug'),
                  type: _get(ref, 'sys.contentType.sys.id'),
                };
              return null;
            })
            .filter(ref => !!ref);

          const [foundRef] = referencesObj.filter(
            ref => ref.slug === slug && ref.type === page,
          );
          if (!foundRef) return false;
        }
        if (id) {
          const referencesId = _get(item, 'fields.referencePages', []).map(
            ref => ref.sys.id,
          );

          if (!referencesId.includes(id)) return false;
        }

        const isAccepted = isClientAccepted(item, clientDetails);
        return isAccepted;
      })
      .sort(
        (a, b) =>
          _get(b, 'fields.client', []).length -
          _get(a, 'fields.client', []).length,
      );
  } else if (type === 'common') {
    [data] = intros
      .filter(item => {
        const commonPages = _get(item, 'fields.commonPages', []);
        if (!commonPages.includes(page)) return false;

        const introTags = _get(item, 'fields.tags', []);
        if (introTags.length && !tagsMatches(introTags, tags)) return false;

        return isClientAccepted(item, clientDetails);
      })
      .sort(
        (a, b) =>
          _get(b, 'fields.client', []).length -
          _get(a, 'fields.client', []).length,
      );
  }
  if (_isEmpty(data)) return null;

  const { description, url, linkName, style } = data.fields;

  const textualLinkNode = url ? (
    <a href={url} target="_blank" className={classes.link}>
      {linkName || 'Learn more'}
      <ArrowRight className={classes.caret} />
    </a>
  ) : null;

  if (style === 'card')
    return (
      <Grid item xs={12}>
        <div
          className={_classNames({
            [classes.root]: true,
            [classes.reference]: type === 'reference',
            [classes.marginTop]: [
              'sign-up',
              'sign-in',
              'assessment-save-intro',
              'user-resources',
            ].includes(page),
            [classes.marginBottom]: [
              'sign-up',
              'sign-in',
              'assessment-save-intro',
              'in-crisis',
              'assessment-results',
            ].includes(page),
            [classes.modal]: modal,
            [classes.horizontalMargin]: page === 'assessment-save-intro',
            [classes.assessmentsColor]:
              type === 'reference' && page === 'assessments',
          })}
        >
          {description ? (
            <RichTextReactRenderer
              color={color}
              data={description}
              paragraphVariant={
                ['sign-up', 'sign-in', 'assessment-save-intro'].includes(page)
                  ? 'overline'
                  : 'body2'
              }
              textualLink={textualLinkNode}
              withHeaderStyles
            />
          ) : (
            <>{textualLinkNode}</>
          )}
        </div>
      </Grid>
    );

  if (style === 'text')
    return (
      <Grid
        item
        xs={12}
        className={_classNames({
          [classes.textBackground]: true,
          [classes.reference]: type === 'reference',
          [classes.marginTop]: [
            'sign-up',
            'sign-in',
            'assessment-save-intro',
          ].includes(page),
          [classes.marginBottom]: ['sign-up', 'sign-in', 'in-crisis'].includes(
            page,
          ),
          [classes.horizontalMargin]: page === 'assessment-save-intro',
        })}
      >
        <RichTextReactRenderer
          data={description}
          paragraphVariant="body2"
          textualLink={textualLinkNode}
          withHeaderStyles
        />
      </Grid>
    );

  if (style === 'plain')
    return (
      <Grid item xs={12} className={classes.plain}>
        <RichTextReactRenderer
          color={color}
          data={description}
          paragraphVariant="body2"
          textualLink={textualLinkNode}
          withHeaderStyles
        />
      </Grid>
    );

  return null;
};

PageIntroBlock.propTypes = {
  page: PropTypes.string,
  slug: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.string,
  modal: PropTypes.bool,
  color: PropTypes.string,
  tags: PropTypes.array,
};

export default PageIntroBlock;
